import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { PageOutro } from "@/components/site/PageEssentials";
import { Section, CTABand, FAQ } from "@/components/site/Blocks";
import { getStateHero } from "@/data/state-heroes";
import { qctFaqs } from "@/lib/hud-data-faqs";
import { getStateQct } from "@/lib/hud-datasets.functions";
import { getStateFmr } from "@/lib/fmr.functions";
import { getState } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getMarketStats } from "@/lib/market-stats.functions";

const qctOptions = (state: string) =>
  queryOptions({
    queryKey: ["stateQct", state],
    queryFn: () => getStateQct({ data: { state } }),
    staleTime: 24 * 60 * 60 * 1000,
  });

const fmrOptions = (state: string) =>
  queryOptions({
    queryKey: ["stateFmr", state],
    queryFn: () => getStateFmr({ data: { state } }),
    staleTime: 60 * 60 * 1000,
  });

export const Route = createFileRoute("/qualified-census-tracts/$state")({
  loader: async ({ params, context }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    context.queryClient.ensureQueryData(qctOptions(state.abbrev));
    context.queryClient.ensureQueryData(fmrOptions(state.abbrev));
    const marketStats = await getMarketStats({ data: { stateSlug: state.slug } }).catch(() => null);
    return { state, marketStats };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: "Qualified Census Tracts by State | Simply Approved Mortgages" }] };
    const url = `/qualified-census-tracts/${params.state}`;
    const title = `QCT in ${state.name} | Simply Approved`;
    const description = `HUD Qualified Census Tracts in ${state.name}, grouped by county. QCT designation unlocks a 30% LIHTC basis boost and signals Section 8 / voucher demand hotspots.`;
    return buildHead({
      title,
      description,
      url,
      type: "article",
      keywords: `${state.name} qualified census tracts, ${state.name} QCT lookup, ${state.name} LIHTC basis boost, ${state.name} 130% qualified basis, ${state.name} Section 8 voucher hotspots, QCT map ${state.name}, ${state.name} affordable housing tracts, ${state.name} census tract poverty rate, ${state.name} LIHTC investing, HUD QCT ${state.name} 2026`,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Qualified Census Tracts", url: "/qualified-census-tracts" },
        { name: state.name, url },
      ],
      service: { name: "Qualified Census Tract Lookup", state: state.name },
      faqs: qctFaqs(state.name),
      image: getStateHero(state.slug)?.src,
      imageAlt: getStateHero(state.slug)?.alt ?? `HUD data for ${state.name}`,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `2026 Qualified Census Tracts — ${state.name}`,
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/datasets/qct.html",
          spatialCoverage: { "@type": "AdministrativeArea", name: state.name },
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: QctStatePage,
  notFoundComponent: () => (
    <SiteShell>
      <Section>
        <p>State not found. <Link to="/qualified-census-tracts" className="text-brand underline">See all states</Link>.</p>
      </Section>
    </SiteShell>
  ),
  errorComponent: ({ error }) => (
    <SiteShell>
      <Section>
        <p className="text-destructive">Couldn't load QCT data: {(error as Error).message}</p>
      </Section>
    </SiteShell>
  ),
});

function QctStatePage() {
  const { state, marketStats } = Route.useLoaderData();

  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD data"
        title={`Qualified Census Tracts in ${state.name} (2026)`}
        subtitle={`HUD's ${state.name} QCT map — the tracts that qualify for LIHTC's 30% basis boost and concentrate Housing Choice Voucher demand, county by county.`}
        primaryLabel="Start My Approval"
        primaryHref="/apply"
        secondaryLabel="See Today's Rates"
        secondaryHref="/rates"
      />
      {/* Hero sits outside Suspense so the <h1> ships in the SSR HTML while the
          live HUD tables stream in. */}
      <Suspense fallback={<TableSkeleton />}>
        <QctData state={state} />
      </Suspense>
      <Section>
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-4 text-center font-display text-2xl font-bold text-foreground">
            {state.name} Qualified Census Tracts — FAQs
          </h2>
          <FAQ items={qctFaqs(state.name)} />
        </div>
      </Section>
      <LocalMarketNews
        stateName={state.name}
        stateAbbrev={state.abbrev}
        stats={marketStats ?? null}
        statsScope="state"
      />
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title={`Buying in a ${state.name} QCT?`}
        body="Send the address and we'll pull the FMR, confirm QCT status, and reply with DSCR pricing feedback."
      />
      <PageOutro />
    </SiteShell>
  );
}

function TableSkeleton() {
  return (
    <Section>
      <div className="mx-auto max-w-5xl animate-pulse space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl border border-border bg-card" />
          ))}
        </div>
        <div className="h-64 rounded-xl border border-border bg-card" />
      </div>
    </Section>
  );
}

function QctData({ state }: { state: { name: string; abbrev: string; slug: string } }) {
  const { data: qct } = useSuspenseQuery(qctOptions(state.abbrev));
  const { data: fmr } = useSuspenseQuery(fmrOptions(state.abbrev));

  // Build a FIPS5 → county display name from FMR data (best available server-side).
  const fipsToName = new Map<string, string>();
  for (const e of fmr.entries) {
    const fips5 = e.fipsCode.slice(0, 5);
    if (!fipsToName.has(fips5)) fipsToName.set(fips5, e.countyName);
  }

  const rows = qct.byCounty;
  const countiesWithQct = rows.length;

  return (
    <Section>
      <div className="mx-auto max-w-5xl">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Stat label="Total QCTs" value={qct.total.toLocaleString()} />
          <Stat label="Counties with a QCT" value={countiesWithQct.toLocaleString()} />
          <Stat label="Data year" value={String(qct.year)} />
        </div>

        <h2 className="mt-12 font-display text-2xl font-bold text-foreground">QCTs by county in {state.name}</h2>
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">County</th>
                <th className="px-4 py-2">FIPS</th>
                <th className="px-4 py-2 text-right">QCTs</th>
                <th className="px-4 py-2">Sample tract GEOIDs</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.countyFips} className="border-t border-border align-top">
                  <td className="px-4 py-2 font-medium text-foreground">
                    {fipsToName.get(r.countyFips) ?? `County ${r.countyFips}`}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.countyFips}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{r.count}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.sampleTracts.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-12 rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-xl font-semibold text-foreground">What is a Qualified Census Tract (QCT)?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            HUD designates a census tract as a QCT when either at least 50% of households have income below 60% AMI, OR
            the tract has a poverty rate of 25% or higher. QCT-designated projects qualify for a <strong>130% qualified
            basis</strong> under LIHTC (the "30% basis boost"). For DSCR / Section 8 investors, QCTs also identify {state.name}
            neighborhoods with the highest concentration of Housing Choice Voucher tenants — the same census tracts that
            consistently rent at or above FMR under HAP contracts.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <Link to="/qualified-census-tracts" className="rounded-md border border-border px-4 py-2 hover:border-brand">All states →</Link>
          <Link to="/hud-fair-market-rents/$state" params={{ state: state.slug }} className="rounded-md border border-border px-4 py-2 hover:border-brand">
            Fair Market Rents in {state.name}
          </Link>
          <Link to="/section-8-loans/$state" params={{ state: state.slug }} className="rounded-md bg-brand px-4 py-2 font-semibold text-primary-foreground hover:bg-brand/90">
            Section 8 DSCR loans in {state.name}
          </Link>
        </div>
      </div>
    </Section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-foreground tabular-nums">{value}</div>
    </div>
  );
}
